import React, { FC } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from '$hooks';
import { ClubSignupFormData } from '$models';
import { Flex } from '$layouts/flex';
import { Grid } from '$layouts/grid';
import { ChildSignupContainer, ChildSignupLegend, CloseButton, StyledInputField } from './style';
import { SvgIcon } from '$components/elements/svg-icon';
import { Select } from '$components/elements/select';

export type ClubSignupChildProps = {
    index: number;
    removeChild?(index: number): void;
    register(field: string, props: any): any;
    errors: FieldErrors<ClubSignupFormData>;
};

export const ClubSignupChild: FC<ClubSignupChildProps> = (props) => {
    const { index, register, removeChild, errors } = props;
    const { translate } = useTranslation();

    return (
        <ChildSignupContainer>
            <ChildSignupLegend>{translate('signup.child.side') + ' ' + (index + 1)}</ChildSignupLegend>

            <Flex column grow>
                {typeof removeChild === 'function' && (
                    <Flex justifyContent="flex-end">
                        <CloseButton type="button" onClick={() => removeChild(index)}>
                            <SvgIcon svg="cross" />
                        </CloseButton>
                    </Flex>
                )}

                <StyledInputField
                    type="text"
                    spacingBottom="medium"
                    label={translate('signup.child.form.name')}
                    error={errors.children?.[index]?.name}
                    {...register(`children.${index}.name`, {
                        required: translate('signup.child.form.nameRequired'),
                    })}
                />

                <Grid flow="row" columns={'1fr 1fr'} columnGap="medium">
                    <StyledInputField
                        type="text"
                        spacingBottom="medium"
                        label={translate('signup.child.form.birthDate')}
                        error={errors.children?.[index]?.birthDate}
                        {...register(`children.${index}.birthDate`, {
                            required: translate('signup.child.form.birthDateRequired'),
                        })}
                        onFocus={(e) => e.target.setAttribute('type', 'date')}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            const target: HTMLInputElement = e.target;
                            if (!target.value) {
                                target.type = 'text';
                            }

                            register(`children.${index}.birthDate`, {
                                required: translate('signup.child.form.birthDateRequired'),
                            }).onBlur(e);
                        }}
                    />

                    <Select
                        label={translate('signup.child.form.genderPlaceholder')}
                        error={errors.children?.[index]?.gender}
                        spacingBottom="medium"
                        {...register(`children.${index}.gender`, {
                            required: translate('signup.child.form.genderValidation'),
                            validate: (x: any) =>
                                (x && x !== translate('signup.child.form.genderPlaceholder')) ||
                                translate('signup.child.form.genderValidation'),
                        })}
                    >
                        <option key={-1} value={-1} disabled={true}>
                            {translate('signup.child.form.genderPlaceholder')}
                        </option>

                        {['Dreng', 'Pige'].map((x, i) => (
                            <option key={i} value={i}>
                                {x}
                            </option>
                        ))}
                    </Select>
                </Grid>

                <Select
                    label={translate('signup.child.form.relationPlaceholder')}
                    error={errors.children?.[index]?.relation}
                    spacingBottom="medium"
                    {...register(`children.${index}.relation`, {
                        required: translate('signup.child.form.relationValidation'),
                        validate: (x: string) =>
                            (x && x !== translate('signup.child.form.relationPlaceholder')) ||
                            translate('signup.child.form.relationValidation'),
                    })}
                >
                    <option key={-1} value={translate('signup.child.form.relationPlaceholder')} disabled={true}>
                        {translate('signup.child.form.relationPlaceholder')}
                    </option>

                    {['Mor', 'Far', 'Bedsteforælder', 'Gavegiver'].map((x) => (
                        <option key={x} value={x}>
                            {x}
                        </option>
                    ))}
                </Select>
            </Flex>
        </ChildSignupContainer>
    );
};
